import { useState } from 'react'
import {
  useDireccionesEntrega,
  useCrearDireccion,
  useEditarDireccion,
  useBorrarDireccion,
  type DireccionEntrega,
  type DatosDireccionEntrega,
} from '../hooks/useClientes'
import { IconoCerrar } from './iconos'

/**
 * Sección "Direcciones de entrega" de la ficha del cliente (multi-reparto).
 *
 * Un cliente de facturación puede tener varias direcciones de entrega: una empresa
 * con varios centros, o un particular que pide agua para su casa y la de un
 * familiar. Se guardan una vez y se reutilizan cada semana; al programar su reparto
 * se elige a cuáles toca. La "referencia" es libre y opcional (p.ej. "Casa de mamá").
 */
export function DireccionesEntregaCliente({
  contactId,
  direccionPrincipal,
}: {
  contactId: string
  direccionPrincipal?: {
    direccion: string | null
    codigoPostal: string | null
    ciudad: string | null
    provincia: string | null
  }
}) {
  const { data, isPending } = useDireccionesEntrega(contactId)
  const crear = useCrearDireccion(contactId)
  const editar = useEditarDireccion(contactId)
  const borrar = useBorrarDireccion(contactId)
  const [editando, setEditando] = useState<number | 'nueva' | null>(null)

  const direcciones = Array.isArray(data) ? data : []
  const textoPrincipal = direccionPrincipal
    ? [direccionPrincipal.direccion, direccionPrincipal.codigoPostal, direccionPrincipal.ciudad].filter(Boolean).join(', ')
    : ''

  const guardar = async (datos: DatosDireccionEntrega, dirId: number | 'nueva') => {
    if (dirId === 'nueva') await crear.mutateAsync(datos)
    else await editar.mutateAsync({ dirId, datos })
    setEditando(null)
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-tinta-apagada">
          Direcciones de entrega
        </p>
        <span className="text-[11px] text-tinta-apagada">
          {1 + direcciones.length} {1 + direcciones.length === 1 ? 'dirección' : 'direcciones'}
        </span>
      </div>

      {/* Dirección PRINCIPAL del cliente (la de su ficha): es su dirección de
          entrega por defecto. Las de abajo son adicionales (multi-reparto). */}
      <div className="mb-2 flex items-start justify-between gap-2 rounded-lg border border-borde bg-lienzo px-3 py-2">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-tinta">
            {textoPrincipal || 'Dirección de la ficha'}
          </p>
          <p className="text-[11px] text-tinta-apagada">Dirección principal (de la ficha)</p>
        </div>
        <span className="shrink-0 rounded-full bg-positivo-fondo px-2 py-0.5 text-[10px] font-semibold text-positivo-tinta">
          Principal
        </span>
      </div>

      <p className="mb-2 text-xs text-tinta-apagada">
        Añade direcciones adicionales para repartir a varios sitios con la misma facturación
        (p. ej. varios centros, o la casa de un familiar).
      </p>

      {isPending ? (
        <p className="text-xs text-tinta-apagada">Cargando direcciones…</p>
      ) : direcciones.length === 0 ? null : (
        <ul className="mb-2 flex flex-col gap-2">
          {direcciones.map((d) =>
            editando === d.id ? (
              <li key={d.id}>
                <FormDireccion
                  inicial={d}
                  guardando={editar.isPending}
                  onGuardar={(datos) => void guardar(datos, d.id)}
                  onCancelar={() => setEditando(null)}
                />
              </li>
            ) : (
              <li
                key={d.id}
                className="flex items-start justify-between gap-2 rounded-lg border border-borde bg-lienzo px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-semibold text-tinta">
                    {d.referencia || d.direccion}
                  </p>
                  <p className="truncate text-[12px] text-tinta-apagada">
                    {[d.direccion, d.codigoPostal, d.ciudad].filter(Boolean).join(', ')}
                  </p>
                  {d.destinatario ? (
                    <p className="truncate text-[11px] text-tinta-apagada">Para: {d.destinatario}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => setEditando(d.id)}
                    aria-label={`Editar ${d.referencia || d.direccion}`}
                    className="text-tinta-apagada transition-colors hover:text-acento"
                  >
                    <IconoEditar className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => void borrar.mutate({ dirId: d.id })}
                    aria-label={`Eliminar ${d.referencia || d.direccion}`}
                    className="text-tinta-apagada transition-colors hover:text-negativo-tinta"
                  >
                    <IconoCerrar className="size-4" />
                  </button>
                </div>
              </li>
            ),
          )}
        </ul>
      )}

      {editando === 'nueva' ? (
        <FormDireccion
          guardando={crear.isPending}
          onGuardar={(datos) => void guardar(datos, 'nueva')}
          onCancelar={() => setEditando(null)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditando('nueva')}
          className="self-start rounded-lg border border-dashed border-acento-borde px-3 py-1.5 text-sm font-medium text-acento-tinta transition-opacity hover:opacity-80"
        >
          + Añadir dirección de entrega
        </button>
      )}
    </div>
  )
}

/** Formulario de alta/edición de una dirección de entrega. */
function FormDireccion({
  inicial,
  guardando,
  onGuardar,
  onCancelar,
}: {
  inicial?: DireccionEntrega
  guardando: boolean
  onGuardar: (datos: DatosDireccionEntrega) => void
  onCancelar: () => void
}) {
  const [referencia, setReferencia] = useState(inicial?.referencia ?? '')
  const [destinatario, setDestinatario] = useState(inicial?.destinatario ?? '')
  const [direccion, setDireccion] = useState(inicial?.direccion ?? '')
  const [codigoPostal, setCodigoPostal] = useState(inicial?.codigoPostal ?? '')
  const [ciudad, setCiudad] = useState(inicial?.ciudad ?? '')
  const [provincia, setProvincia] = useState(inicial?.provincia ?? '')

  const campo =
    'w-full rounded-lg border border-borde bg-lienzo px-3 py-2 text-[13px] text-tinta outline-none placeholder:text-tinta-apagada focus:border-acento'

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-dashed border-acento-borde bg-acento-tinta-suave/40 p-3">
      <input
        value={referencia}
        onChange={(e) => setReferencia(e.target.value)}
        placeholder="Referencia (opcional): Casa de mamá, Colegio San José…"
        aria-label="Referencia"
        className={campo}
      />
      <input
        value={destinatario}
        onChange={(e) => setDestinatario(e.target.value)}
        placeholder="Destinatario (opcional): quién recibe"
        aria-label="Destinatario"
        className={campo}
      />
      <input
        value={direccion}
        onChange={(e) => setDireccion(e.target.value)}
        placeholder="Dirección *"
        aria-label="Dirección"
        className={campo}
      />
      <div className="flex gap-2">
        <input
          value={codigoPostal}
          onChange={(e) => setCodigoPostal(e.target.value)}
          placeholder="Código postal"
          aria-label="Código postal"
          className={campo}
        />
        <input
          value={ciudad}
          onChange={(e) => setCiudad(e.target.value)}
          placeholder="Ciudad"
          aria-label="Ciudad"
          className={campo}
        />
      </div>
      <input
        value={provincia}
        onChange={(e) => setProvincia(e.target.value)}
        placeholder="Provincia"
        aria-label="Provincia"
        className={campo}
      />
      <div className="flex gap-2">
        <button
          type="button"
          disabled={guardando || !direccion.trim()}
          onClick={() =>
            onGuardar({ referencia, destinatario, direccion, codigoPostal, ciudad, provincia })
          }
          className="rounded-lg bg-acento px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {guardando ? 'Guardando…' : 'Guardar dirección'}
        </button>
        <button
          type="button"
          onClick={onCancelar}
          className="rounded-lg border border-borde px-3 py-1.5 text-sm font-medium text-tinta transition-colors hover:bg-lienzo"
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}

/** Icono de editar (lápiz), coherente con los SVG outline del resto. */
function IconoEditar({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  )
}
